import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { HighlightOff as RefreshIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { get } from 'lodash';
import moment from 'moment';

import { SvgIconWrapper, Typography, TypographyI18n } from 'components/v2';

import IconDotsFlashing from '../WIP/ui-core/IconDotsFlashing';
import { EMessageType, IMessage } from '../WIP/ui-model/types';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginBottom: theme.spacing.unit,
      '&$outbound': {
        justifyContent: 'flex-end',
        marginBottom: theme.spacing.unit * 1.5,
        marginTop: theme.spacing.unit,
      },
    },
    wrapperRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      '&:hover $timestamp': {
        opacity: 1,
      },
    },
    wrapperColumn: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: theme.spacing.unit,
      '&$outbound': {
        alignItems: 'flex-end',
      },
      '&$extraMargin': {
        marginLeft: theme.spacing.unit * 4,
      },
    },
    messageWrapper: {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      minHeight: 24,
      borderRadius: 8,
      fontWeight: 500,
      padding: `${theme.spacing.unit * 1.5}px ${theme.spacing.unit * 2}px`,
      '&$outbound': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        alignItems: 'flex-end',
      },
    },
    messageContent: {
      color: 'inherit',
      fontWeight: 'inherit',
      width: '100%',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      wordWrap: 'break-word',
      '& a': {
        color: 'inherit !important',
      },
    },
    marginTop: {
      marginTop: theme.spacing.unit,
    },
    wrapperOutboundExtras: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row-reverse',
      '&$includeTimestamp': {
        marginBottom: theme.spacing.unit * 2.5,
      },
    },
    timestamp: {
      cursor: 'default',
      opacity: 0,
      '-webkit-transition': 'all .3s ease',
      transition: 'all .3s ease',
      '-webkit-transition-delay': '.35s',
      transitionDelay: '.35s',
      alignSelf: 'flex-end',
      marginBottom: theme.spacing.unit * 1.5,
      whiteSpace: 'nowrap',
    },
    bottomTimestamp: {
      marginTop: theme.spacing.unit / 4,
    },
    inboundMessageWrapper: {
      display: 'flex',
      gap: `${theme.spacing.unit}px`,
    },
    revertBtn: {
      marginLeft: theme.spacing.unit,
      fontSize: theme.typography.pxToRem(16),
      cursor: 'pointer',
      marginBottom: theme.spacing.unit * 1.5,
      opacity: 0.8,
      transition: 'opacity .3s ease',
      '&:hover': {
        opacity: 1,
      },
      '&:hover + $timestamp': {
        opacity: 0,
      },
    },
    typingAnimationWrapper: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      padding: `0 ${theme.spacing.unit}px`,
    },
    createdByName: {
      lineHeight: '16px',
      marginBottom: theme.spacing.unit,
    },
    outbound: {},
    includeTimestamp: {},
    extraMargin: {},
  });

export interface MessageProps extends WithStyles<typeof styles> {
  variant?: 'default' | 'typing';
  message?: IMessage;
  avatarInbound?: React.ReactNode;
  includeTimestamp?: boolean;
  includeRevert?: boolean;
  onRevert?: () => void;
  prevMessage?: any;
  nextMessage?: any;
}

const Message: React.FC<MessageProps> = ({
  classes,
  children,
  variant = 'default',
  message,
  avatarInbound,
  includeTimestamp,
  includeRevert,
  prevMessage,
  nextMessage,
  onRevert,
}) => {
  const isOutbound = variant === 'default' && get(message, 'type') === EMessageType.Outbound;
  const isInbound = !isOutbound;
  const createdByName = get(message, 'createdBy.name');
  const nextCreatedByName = get(nextMessage, 'createdBy.name');
  const prevCreatedByName = get(prevMessage, 'createdBy.name');

  const messageType = get(message, 'type');
  const nextMessageType = get(nextMessage, 'type');
  const prevMessageType = get(prevMessage, 'type');

  const isScheduler = get(message, 'metadata.item.type') === 'scheduler';
  const isNextMessageBookStatus = get(nextMessage, 'metadata.item.type') === 'book_status_component';

  const isPrevMessageScheduler = get(prevMessage, 'metadata.item.type') === 'scheduler';
  const isPrevMessageManageAppt = get(prevMessage, 'metadata.item.type') === 'manageAppointment';
  const shouldIncludeRevert = includeRevert && !isPrevMessageManageAppt && !isPrevMessageScheduler;

  const shouldShowName =
    createdByName &&
    variant !== 'typing' &&
    (createdByName !== prevCreatedByName ||
      messageType !== prevMessageType ||
      get(message, 'metadata.forceName', false));

  const shouldShowAvatar =
    isInbound &&
    (messageType !== nextMessageType ||
      createdByName !== nextCreatedByName ||
      variant === 'typing' ||
      isScheduler ||
      isNextMessageBookStatus);

  const shouldHaveExtraMargin = !shouldShowAvatar && isInbound;

  return (
    <div className={classnames(classes.root, { [classes.outbound]: isOutbound })}>
      <div className={classnames(classes.wrapperRow, { [classes.outbound]: isOutbound })}>
        {/* Option to return to the message */}
        {isOutbound && (
          <div
            className={classnames(classes.wrapperOutboundExtras, {
              [classes.includeTimestamp]: includeTimestamp,
            })}
          >
            {shouldIncludeRevert && message && (
              <SvgIconWrapper
                Icon={RefreshIcon}
                data-lh-id={`button-revert-${message._id}`}
                onClick={onRevert}
                className={classes.revertBtn}
              />
            )}
            {message && message.createdAt && (
              <Typography className={classes.timestamp} variant="caption" color="supportSteel" noWrap>
                {moment(message.createdAt).format('LT')}
              </Typography>
            )}
          </div>
        )}
        {shouldShowAvatar && avatarInbound}
        <div
          className={classnames(classes.wrapperColumn, {
            [classes.outbound]: isOutbound,
            [classes.extraMargin]: shouldHaveExtraMargin,
          })}
        >
          {isInbound && shouldShowName && (
            <Typography className={classes.createdByName} variant="caption" color="supportSteel">
              {createdByName}
            </Typography>
          )}
          <div className={classnames({ [classes.inboundMessageWrapper]: isInbound })}>
            <div className={classnames(classes.messageWrapper, { [classes.outbound]: isOutbound })}>
              {variant === 'typing' ? (
                <div className={classes.typingAnimationWrapper}>
                  <IconDotsFlashing />
                </div>
              ) : (
                <>
                  {message && (
                    <TypographyI18n variant="body1" className={classes.messageContent}>
                      {message.text}
                    </TypographyI18n>
                  )}
                  {children && <div className={classnames({ [classes.marginTop]: message?.text })}>{children}</div>}
                </>
              )}
            </div>
            {isInbound && message && message.createdAt && (
              <Typography className={classes.timestamp} variant="caption" color="supportSteel">
                {moment(message.createdAt).format('LT')}
              </Typography>
            )}
          </div>
          {includeTimestamp && message && (
            <Typography className={classes.bottomTimestamp} variant="caption" color="supportSteel">
              {moment(message.createdAt).format('LT')}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export default withStyles(styles, { name: 'Message' })(Message);
